'use client'

import { useId, useRef, useState } from 'react'
import { cn, Input } from '@nwwp/ui-shadcn'
import { CATEGORIES, categoryByKey, MIN_TOP } from '../_lib/data'
import type { CategoryKey, ItemMove, SpendItem } from '../types'

function ExpenseChip({
  item,
  selected,
  onToggle,
  onReassign,
}: {
  item: SpendItem
  selected: boolean
  onToggle: () => void
  onReassign: (cat: CategoryKey) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuId = useId()
  const toggleRef = useRef<HTMLButtonElement>(null)
  // Reassignment only matters once the expense counts toward a result.
  const canReassign = selected && !!item.pair

  const closeMenu = (refocus: boolean) => {
    setMenuOpen(false)
    if (refocus) toggleRef.current?.focus()
  }

  return (
    <span
      className="relative inline-flex"
      onKeyDown={(event) => {
        if (menuOpen && event.key === 'Escape') closeMenu(true)
      }}
      // Focus leaving the chip closes the menu: that covers tabbing past it and clicking
      // anywhere else on the page, without a document-level listener.
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false)
      }}
    >
      <button
        type="button"
        aria-pressed={selected}
        onClick={onToggle}
        className={cn(
          'rounded-full border px-3.5 py-2 text-body-sm',
          selected
            ? 'border-green-darker bg-green-surface font-bold text-gray-darker'
            : 'border-smoke bg-card text-neutral-darker hover:border-neutral-dark'
        )}
      >
        {item.t}
      </button>

      {canReassign && (
        <>
          <button
            type="button"
            ref={toggleRef}
            onClick={() => setMenuOpen((open) => !open)}
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            aria-label={`Change which value ${item.t} counts toward`}
            className="ml-1 flex size-7 items-center justify-center self-center rounded-full bg-bone-warm text-neutral-darker hover:bg-green-lighter-2 hover:text-black"
          >
            <span aria-hidden="true">⇄</span>
          </button>

          {menuOpen && (
            <span
              id={menuId}
              className="absolute top-full left-0 z-10 mt-1 flex min-w-60 flex-col rounded-xl border border-smoke bg-card p-1 shadow-lg"
            >
              {item.pair?.map((pk) => {
                const isCurrent = pk === item.cat
                return (
                  <button
                    key={pk}
                    type="button"
                    onClick={() => {
                      if (!isCurrent) onReassign(pk)
                      closeMenu(true)
                    }}
                    className={cn(
                      'rounded-lg px-3 py-2 text-left text-body-sm',
                      isCurrent ? 'bg-green-surface text-gray-darker' : 'text-neutral-darker hover:bg-bone-warm'
                    )}
                  >
                    {isCurrent && <span aria-hidden="true">✓ </span>}
                    {categoryByKey(pk).n}
                  </button>
                )
              })}
            </span>
          )}
        </>
      )}
    </span>
  )
}

function AddYourOwn({ onAdd }: { onAdd: (label: string) => void }) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')

  const save = () => {
    if (!value.trim()) return
    onAdd(value)
    setValue('')
    setOpen(false)
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full border border-dashed border-neutral-dark px-3.5 py-2 text-body-sm text-neutral-darker hover:border-green-darker hover:text-green-darker"
      >
        + Add your own
      </button>
    )
  }

  return (
    <span className="inline-flex items-center gap-2">
      <Input
        autoFocus
        aria-label="Name your expense"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            save()
          }
          if (e.key === 'Escape') setOpen(false)
        }}
        className="w-50"
      />
      <button type="button" onClick={save} className="text-body-sm-bold text-green-dark underline">
        Save
      </button>
      <button type="button" onClick={() => setOpen(false)} className="text-body-sm text-neutral-darker underline">
        Cancel
      </button>
    </span>
  )
}

function CategoryGroup({
  k,
  rank,
  items,
  picked,
  moves,
  onToggle,
  onReassign,
  onUndo,
  onDismiss,
  onAddCustom,
}: {
  k: CategoryKey
  rank?: number
  items: SpendItem[]
  picked: Record<string, true>
  moves: ItemMove[]
  onToggle: (id: string) => void
  onReassign: (id: string, cat: CategoryKey) => void
  onUndo: (id: string) => void
  onDismiss: (id: string) => void
  onAddCustom: (cat: CategoryKey, label: string) => void
}) {
  const category = categoryByKey(k)
  const mine = items.filter((it) => it.cat === k)
  const selectedCount = mine.filter((it) => picked[it.id]).length
  const outbound = moves.filter((m) => m.from === k)

  return (
    <section className="rounded-2xl border border-smoke bg-card p-4 tablet:p-5">
      <div className="flex items-start gap-3">
        {rank !== undefined && (
          <span
            aria-hidden="true"
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full text-body-xs font-bold tabular-nums',
              rank === 1 ? 'bg-green-lighter-2 text-black' : 'bg-bone-warm text-neutral-darker'
            )}
          >
            {rank}
          </span>
        )}
        <div className="flex-1">
          <h3 className="text-body-sm-bold text-gray-darker">{category.n}</h3>
          <p className="text-body-xs text-neutral-darker">{category.d}</p>
        </div>
        {selectedCount > 0 && (
          <span className="text-body-xs font-bold text-green-dark tabular-nums">{selectedCount} selected</span>
        )}
      </div>

      <div className="mt-3.5 flex flex-wrap items-center gap-2">
        {mine.map((it) => (
          <ExpenseChip
            key={it.id}
            item={it}
            selected={!!picked[it.id]}
            onToggle={() => onToggle(it.id)}
            onReassign={(cat) => onReassign(it.id, cat)}
          />
        ))}
        <AddYourOwn onAdd={(label) => onAddCustom(k, label)} />
      </div>

      {outbound.map((m) => {
        const item = items.find((x) => x.id === m.id)
        return (
          <p key={m.id} className="mt-2.5 flex flex-wrap items-center gap-2 text-body-xs text-neutral-darker">
            <span>
              {item?.t} → {categoryByKey(m.to).n}
            </span>
            <button
              type="button"
              onClick={() => onUndo(m.id)}
              className="text-body-xs font-bold text-green-dark underline"
            >
              Undo
            </button>
            <button type="button" onClick={() => onDismiss(m.id)} className="text-body-xs underline">
              Dismiss
            </button>
          </p>
        )
      })}
    </section>
  )
}

export function PickStep({
  ranked,
  items,
  picked,
  moves,
  onToggle,
  onReassign,
  onUndo,
  onDismiss,
  onAddCustom,
  onBack,
  onNext,
}: {
  ranked: CategoryKey[]
  items: SpendItem[]
  picked: Record<string, true>
  moves: ItemMove[]
  onToggle: (id: string) => void
  onReassign: (id: string, cat: CategoryKey) => void
  onUndo: (id: string) => void
  onDismiss: (id: string) => void
  onAddCustom: (cat: CategoryKey, label: string) => void
  onBack: () => void
  onNext: () => void
}) {
  const [showOther, setShowOther] = useState(false)
  const unranked = CATEGORIES.map((c) => c.k).filter((k) => !ranked.includes(k))
  const pickedCount = Object.keys(picked).length
  const blocked = pickedCount < MIN_TOP

  const groupProps = { items, picked, moves, onToggle, onReassign, onUndo, onDismiss, onAddCustom }

  return (
    <div>
      <h1 className="text-title-md text-gray-darker">What you actually spent money on.</h1>
      <p className="mt-3 max-w-150 text-body text-neutral-darker">
        Tap every expense from the last few months. Selected items turn bold.
      </p>

      <div className="mt-6 flex flex-col gap-3">
        {ranked.map((k, i) => (
          <CategoryGroup key={k} k={k} rank={i + 1} {...groupProps} />
        ))}
      </div>

      {unranked.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowOther((open) => !open)}
            aria-expanded={showOther}
            className="flex w-full items-center justify-between gap-3 rounded-2xl border border-smoke bg-card px-4 py-3.5 text-left text-body-sm text-neutral-darker"
          >
            <span>
              {showOther ? 'Hide the values you did not rank' : 'Spend money in an area you did not rank? Open it here'}
            </span>
            <span className="shrink-0 text-body-xs">{unranked.length} areas</span>
          </button>

          {showOther && (
            <div className="mt-3 flex flex-col gap-3">
              {unranked.map((k) => (
                <CategoryGroup key={k} k={k} {...groupProps} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button type="button" onClick={onBack} className="text-body-sm-bold text-green-dark underline">
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={blocked}
          className="rounded-full bg-green-lighter-2 px-7 py-3.5 text-body-sm-bold text-black uppercase disabled:opacity-45"
        >
          Next
        </button>
        <p className="text-body-sm text-neutral-darker">
          {blocked ? `Select at least ${MIN_TOP} expenses to continue.` : `${pickedCount} selected`}
        </p>
      </div>
    </div>
  )
}
